(() => {
  if (window.beddingSizeGuideInitialized) return;

  window.beddingSizeGuideInitialized = true;

  const dialogSelector = '[data-bsg-dialog]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sheetLayout = window.matchMedia('(max-width: 699px)');

  const dragSlop = 7;
  const dragDismissRatio = .25;
  const dragDismissMin = 80;
  const dragDismissMax = 180;
  const dragFlickSpeed = .55;
  const dragFlickMinOffset = 24;
  const dragSpringMs = 240;
  const dragExitMs = 190;
  const interactiveSelector = 'a[href], button, input, select, textarea, label';

  let drag = null;
  let suppressClickUntil = 0;
  let dragZoneFrame = 0;

  const setPageScrollLocked = (locked) => {
    document.documentElement.classList.toggle('bsg-scroll-locked', locked);
    document.body.classList.toggle('bsg-scroll-locked', locked);
  };

  const clearSurfaceDrag = (surface) => {
    if (!surface) return;

    surface.style.transform = '';
    surface.style.transition = '';
  };

  const releasePointer = (state) => {
    if (!state?.captured) return;

    state.captured = false;

    try {
      state.surface.releasePointerCapture(state.pointerId);
    } catch (error) {
      /* The pointer is already gone. */
    }
  };

  const endDrag = () => {
    const state = drag;

    drag = null;
    releasePointer(state);

    return state;
  };

  const applyDragOffset = (state, offset) => {
    state.offset = offset;
    state.surface.style.transform = offset > 0 ? `translateY(${offset}px)` : '';
  };

  const springBack = (state) => {
    const surface = state.surface;

    if (!state.committed || state.offset <= 0 || reducedMotion.matches) {
      clearSurfaceDrag(surface);
      state.dialog.removeAttribute('data-bsg-dragging');
      return;
    }

    surface.style.transition = `transform ${dragSpringMs}ms cubic-bezier(.22,.61,.36,1)`;
    void surface.offsetHeight;
    surface.style.transform = 'translateY(0px)';

    const settle = (event) => {
      if (event && event.target !== surface) return;

      surface.removeEventListener('transitionend', settle);
      window.clearTimeout(surface.bsgSpringTimer);
      surface.bsgSpringTimer = null;
      surface.bsgSpringSettle = null;
      clearSurfaceDrag(surface);
      state.dialog.removeAttribute('data-bsg-dragging');
    };

    surface.bsgSpringSettle = settle;
    surface.addEventListener('transitionend', settle);
    surface.bsgSpringTimer = window.setTimeout(settle, dragSpringMs + 60);
  };

  /* Hands the travelled distance to the CSS exit animation so the sheet keeps
     moving from where the finger left it. Must run in the same task as the
     data-bsg-closing flip so no paint lands in between. */
  const handOffDrag = (state) => {
    const remaining = Math.max(0, 1 - state.offset / Math.max(state.height, 1));

    state.dialog.style.setProperty('--bsg-drag-offset', `${Math.round(state.offset)}px`);
    state.dialog.style.setProperty('--bsg-exit-duration', `${Math.round(Math.max(120, dragExitMs * remaining))}ms`);
    clearSurfaceDrag(state.surface);
    state.dialog.removeAttribute('data-bsg-dragging');
  };

  const syncDragZone = (dialog) => {
    const content = dialog?.querySelector('.bsg-content');
    if (!content) return;

    content.toggleAttribute(
      'data-bsg-drag-zone',
      sheetLayout.matches && content.scrollHeight - content.clientHeight <= 1
    );
  };

  const syncOpenDragZones = () => {
    dragZoneFrame = 0;
    document.querySelectorAll(`${dialogSelector}[open]`).forEach(syncDragZone);
  };

  const finishClose = (dialog) => {
    if (!dialog.open) return;

    if (dialog.bsgAnimationEndHandler) {
      dialog.removeEventListener('animationend', dialog.bsgAnimationEndHandler);
      dialog.bsgAnimationEndHandler = null;
    }

    dialog.removeAttribute('data-bsg-closing');
    dialog.removeAttribute('data-bsg-dragging');
    dialog.style.removeProperty('--bsg-drag-offset');
    dialog.style.removeProperty('--bsg-exit-duration');
    dialog.querySelector('.bsg-content')?.removeAttribute('data-bsg-drag-zone');
    dialog.close();
    setPageScrollLocked(Boolean(document.querySelector(`${dialogSelector}[open]`)));

    const opener = dialog.bsgOpener;
    dialog.bsgOpener = null;

    if (opener?.isConnected) opener.focus();
  };

  const closeDialog = (dialog) => {
    if (!dialog?.open || dialog.hasAttribute('data-bsg-closing')) return;

    if (drag?.dialog === dialog) handOffDrag(endDrag());

    if (reducedMotion.matches) {
      finishClose(dialog);
      return;
    }

    dialog.setAttribute('data-bsg-closing', '');
    dialog.bsgAnimationEndHandler = () => finishClose(dialog);
    dialog.addEventListener('animationend', dialog.bsgAnimationEndHandler, { once: true });
    window.setTimeout(() => finishClose(dialog), 260);
  };

  document.addEventListener('pointerdown', (event) => {
    suppressClickUntil = 0;

    if (!sheetLayout.matches || !event.isPrimary || event.button > 0) return;
    if (!(event.target instanceof Element)) return;

    const surface = event.target.closest('.bsg-surface');
    const dialog = surface?.closest(dialogSelector);

    if (!surface || !dialog?.open || dialog.hasAttribute('data-bsg-closing')) return;
    if (event.target.closest(interactiveSelector)) return;

    const content = dialog.querySelector('.bsg-content');
    const insideContent = Boolean(content?.contains(event.target));

    if (insideContent && event.pointerType === 'mouse') return;

    if (surface.bsgSpringSettle) surface.bsgSpringSettle();

    drag = {
      pointerId: event.pointerId,
      dialog: dialog,
      surface: surface,
      content: content,
      insideContent: insideContent,
      startX: event.clientX,
      startY: event.clientY,
      lastY: event.clientY,
      lastTime: event.timeStamp,
      speed: 0,
      height: surface.getBoundingClientRect().height,
      offset: 0,
      committed: false,
      captured: false,
    };
  }, true);

  document.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;

    if (!drag.committed) {
      if (Math.abs(deltaX) > dragSlop && Math.abs(deltaX) > Math.abs(deltaY)) {
        drag = null;
        return;
      }

      if (deltaY < -dragSlop) {
        drag = null;
        return;
      }

      if (deltaY < dragSlop) return;

      if (drag.insideContent && drag.content && drag.content.scrollTop > 0) {
        drag = null;
        return;
      }

      drag.committed = true;
      drag.dialog.setAttribute('data-bsg-dragging', '');

      try {
        drag.surface.setPointerCapture(drag.pointerId);
        drag.captured = true;
      } catch (error) {
        /* Fall back to document-level tracking. */
      }

      if (event.pointerType === 'mouse') window.getSelection()?.removeAllRanges();
    }

    const elapsed = event.timeStamp - drag.lastTime;

    if (elapsed > 0) {
      drag.speed = (event.clientY - drag.lastY) / elapsed;
      drag.lastY = event.clientY;
      drag.lastTime = event.timeStamp;
    }

    applyDragOffset(drag, Math.max(0, deltaY - dragSlop));
  }, true);

  document.addEventListener('pointerup', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;

    const state = endDrag();

    if (!state.committed) return;

    /* A committed drag must not activate whatever was under the finger. The
       synthesised click follows within a few hundred ms, so bound the window
       rather than latching a flag a keyboard click could later trip over. */
    suppressClickUntil = event.timeStamp + 500;

    const distance = Math.min(Math.max(state.height * dragDismissRatio, dragDismissMin), dragDismissMax);
    const flicked = state.speed >= dragFlickSpeed && state.offset >= dragFlickMinOffset;

    if (state.dialog.open && (state.offset >= distance || flicked)) {
      handOffDrag(state);
      closeDialog(state.dialog);
      return;
    }

    springBack(state);
  }, true);

  document.addEventListener('pointercancel', (event) => {
    if (!drag || event.pointerId !== drag.pointerId) return;

    springBack(endDrag());
  }, true);

  document.addEventListener('click', (event) => {
    if (!suppressClickUntil || event.timeStamp > suppressClickUntil) return;

    suppressClickUntil = 0;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  sheetLayout.addEventListener('change', () => {
    const state = endDrag();

    if (state) {
      clearSurfaceDrag(state.surface);
      state.dialog.removeAttribute('data-bsg-dragging');
    }

    syncOpenDragZones();
  });

  window.addEventListener('resize', () => {
    if (dragZoneFrame) return;

    dragZoneFrame = window.requestAnimationFrame(syncOpenDragZones);
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-bsg-target]');

    if (opener) {
      const targetSelector = opener.getAttribute('data-bsg-target');
      const dialog = targetSelector ? document.querySelector(targetSelector) : null;

      if (!(dialog instanceof HTMLDialogElement)) return;

      event.preventDefault();
      dialog.bsgOpener = opener;
      const content = dialog.querySelector('.bsg-content');
      dialog.showModal();
      if (content) content.scrollTop = 0;
      window.requestAnimationFrame(() => {
        if (!dialog.open) return;
        if (content) content.scrollTop = 0;
        syncDragZone(dialog);
      });
      setPageScrollLocked(true);
      dialog.querySelector('.bsg-surface')?.focus({ preventScroll: true });
      return;
    }

    const closeButton = event.target.closest('[data-bsg-close]');

    if (closeButton) {
      closeDialog(closeButton.closest(dialogSelector));
      return;
    }

    if (event.target.matches(dialogSelector)) closeDialog(event.target);
  });

  document.addEventListener('cancel', (event) => {
    if (!event.target.matches(dialogSelector)) return;

    event.preventDefault();
    closeDialog(event.target);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;

    const dialog = document.querySelector(`${dialogSelector}[open]`);
    if (!dialog) return;

    event.preventDefault();
    closeDialog(dialog);
  });
})();
